import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ProgressBarMixin } from './StyledComponents';

export const StyledProgressBar = styled.div`
    ${ProgressBarMixin}
`;

const PROGRESS_METER_SIZES = {
    sm: 'sm',
    md: 'md',
};

const propTypes = {
    /**
     * The current progress value. Do not use a percentage. Instead, use a
     * number between the minValue and maxValue.
     */
    value: PropTypes.number.isRequired,
    /** The value when no progress has been made. */
    minValue: PropTypes.number,
    /** The value when progress has been completed. */
    maxValue: PropTypes.number,
    /** Determines the height of the bar. */
    size: PropTypes.oneOf(Object.keys(PROGRESS_METER_SIZES)),
};
const defaultProps = {
    minValue: 0,
    maxValue: 100,
    size: 'sm',
};


/**
 * Progress bars help users understand the length of a process and how far they
 * are from the completion of that process.
 *
 * @since {{LATEST_VERSION}}
 */
const ProgressBar = React.forwardRef(({
    value, minValue, maxValue, size, ...rest
}, ref) => {
    /* Used to set indicator bar styles. Also ensures % can't be less than 0 nor more than 100 */
    let percentComplete = 0;
    if (value >= maxValue) {
        percentComplete = 100;
    } else if (value > minValue && value < maxValue) {
        percentComplete = ((value - minValue) / (maxValue - minValue)) * 100;
    }

    return (
        <StyledProgressBar
            role="progressbar"
            tabIndex="0"
            value={value}
            minValue={minValue}
            maxValue={maxValue}
            size={size}
            aria-valuenow={value}
            aria-valuemin={minValue}
            aria-valuemax={maxValue}
            percentComplete={percentComplete}
            ref={ref}
            {...rest}
        />
    );
});

ProgressBar.displayName = 'ProgressBar';

ProgressBar.propTypes = propTypes;

ProgressBar.defaultProps = defaultProps;

/** @component */
export default ProgressBar;
